import { makeAutoObservable } from 'mobx'

import { AccountApi } from '../api'
import type {
  Account,
  AssetsLiabilities,
  DetailedAccount,
  GainsLosses,
  HistoricalAccountBalance,
  MonthlyAccountBalance,
  NetWorth,
  NetWorthData,
} from '../types'
import {
  filterAccounts,
  flattenBalances,
  buildAssetsLiabilities,
  buildChartData,
  buildGainsLosses,
  getLatestNetWorthData,
  sortByYearMonthAscending,
  toMonthlyAccountBalance,
} from '../utils/NetWorthUtils'
import { isFeatureEnabled } from '../utils/UserUtils'

import { GlobalStore } from './GlobalStore'

// Looks wrong next to the MD_* flags, but isn't: this is the seeded feature_name. Do not rename.
export const HISTORICAL_ACCOUNT_BALANCES = 'Historical account balances'

export class NetWorthStore {
  globalStore: GlobalStore
  accountsApi: AccountApi = new AccountApi('/', '')

  monthlyAccountBalances: MonthlyAccountBalance[] = []
  monthlyAccountBalancesLoaded: boolean = false
  hoveredIndex: number = -1
  selectedIndex: number = -1
  timeframe: number = 6 // default timeframe is 6 months

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore
    this.accountsApi = new AccountApi(
      globalStore.endpoint,
      globalStore.sessionToken,
      globalStore.onError,
    )
    makeAutoObservable(this)
  }

  get isNetWorthDataLoaded(): boolean {
    return this.globalStore.accountStore.isAccountDataLoaded && this.monthlyAccountBalancesLoaded
  }

  get netWorthAccounts(): DetailedAccount[] {
    return filterAccounts(
      this.globalStore.globalUiStore.selectedAccounts,
      this.globalStore.accountStore.members,
    )
  }

  get netWorthData(): NetWorthData {
    return {
      accounts: this.netWorthAccounts,
      monthlyAccountBalances: this.monthlyAccountBalances,
    }
  }

  get netWorthChartData(): NetWorth[] {
    return buildChartData(this.netWorthAccounts, this.monthlyAccountBalances, this.timeframe)
  }

  get selectedData(): NetWorth | undefined {
    if (!this.netWorthChartData || this.netWorthChartData.length === 0) return undefined
    return this.selectedIndex === -1
      ? getLatestNetWorthData(this.netWorthChartData)
      : this.netWorthChartData[this.selectedIndex]
  }

  get hoveredData(): NetWorth | undefined {
    return this.hoveredIndex >= 0 ? this.netWorthChartData[this.hoveredIndex] : undefined
  }

  get gainsLosses(): GainsLosses {
    return buildGainsLosses(this.selectedData)
  }

  get isHistoricalBalancesEnabled(): boolean {
    return isFeatureEnabled(this.globalStore.userStore.userFeatures, HISTORICAL_ACCOUNT_BALANCES)
  }

  get assetsLiabilities(): AssetsLiabilities {
    const { assets, liabilities, totalAssets, totalLiabilities } = buildAssetsLiabilities(
      this.netWorthData.accounts,
    )
    return {
      netWorth: getLatestNetWorthData(this.netWorthChartData),
      assets,
      liabilities,
      totalAssets,
      totalLiabilities,
    }
  }

  loadNetWorthData = async () => {
    if (!this.globalStore.accountStore.isAccountDataLoaded) {
      await this.globalStore.accountStore.loadAccountData()
    }

    if (!this.globalStore.userStore.userFeaturesLoaded) {
      await this.globalStore.userStore.loadUserFeatures()
    }

    const fetchBalances = this.isHistoricalBalancesEnabled
      ? (guid: string) => this.getHistoricalAccountBalances(guid)
      : (guid: string) => this.getMonthlyAccountBalances(guid)

    const accountBalances = await Promise.all(
      this.netWorthAccounts.map((account: Account) => fetchBalances(account.guid)),
    )

    const monthlyAccountBalances = accountBalances
      .reduce(flattenBalances, [])
      .sort(sortByYearMonthAscending)

    this.setMonthlyAccountBalances(monthlyAccountBalances)
  }

  getMonthlyAccountBalances = async (accountGuid: string) => {
    try {
      const balances = await this.accountsApi.getMonthlyAccountBalances(accountGuid)
      return balances as MonthlyAccountBalance[]
    } catch (error) {
      console.error('Error occurred while fetching monthly account balances:', error)
      this.globalStore.captureException(error, { store: 'NetWorthStore' })
      return []
    }
  }

  getHistoricalAccountBalances = async (accountGuid: string): Promise<MonthlyAccountBalance[]> => {
    try {
      const balances: HistoricalAccountBalance[] =
        await this.accountsApi.getHistoricalAccountBalances(accountGuid)
      return balances.map(toMonthlyAccountBalance)
    } catch (error) {
      console.error('Error occurred while fetching historical account balances:', error)
      this.globalStore.captureException(error, { store: 'NetWorthStore' })
      return []
    }
  }

  setHoveredIndex = (index: number) => (this.hoveredIndex = index)

  setMonthlyAccountBalances = (balances: MonthlyAccountBalance[]) => {
    this.monthlyAccountBalances = balances
    this.monthlyAccountBalancesLoaded = true
  }

  setSelectedIndex = (index: number) => (this.selectedIndex = index)
  setTimeframe = (months: number) => (this.timeframe = months)
}
